import { Text } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useMemo } from 'react';
import { PreConsultationLeadRow } from '../services/preConsultationRows';
import { PersonCell } from './PersonCell';
import { formatListDate, lastCommunicationColumn, nextTaskColumn } from './sharedColumns';

/**
 * Columns for the pre-consultation queue.
 *
 * Its own file so the tab component exports only its component, and so these can
 * be mounted in a table by a test without standing up a Medplum client.
 *
 * EVERY accessor tolerates a missing row. That is not defensive habit — it is a
 * hard requirement mantine-react-table imposes and does not document: on a table
 * with ZERO rows it still invokes `accessorFn` while resolving the initial sort
 * direction, and passes nothing. An accessor written as `row.patient.name` throws
 * `Cannot read properties of undefined`, React unmounts to the error boundary,
 * and the tab renders "Something went wrong" — before its query has even fired,
 * so the failure looks like a dead page rather than an empty queue.
 *
 * This shipped to staging. Unit tests over the row builders cannot catch it:
 * they never mount a table. `preConsultationColumns.test.tsx` renders one with an
 * empty dataset, which is the only shape that reproduces it.
 */

export function usePreConsultationColumns(now: Date): MRT_ColumnDef<PreConsultationLeadRow>[] {
  return useMemo(
    () => [
      {
        id: 'firstName',
        header: 'First name',
        accessorFn: (row) => row?.patient?.name?.[0]?.given?.join(' ') ?? '',
      },
      {
        id: 'lastName',
        header: 'Last name',
        accessorFn: (row) => row?.patient?.name?.[0]?.family ?? '',
      },
      {
        id: 'location',
        header: 'Location',
        accessorFn: (row) => row?.location ?? '—',
      },
      {
        id: 'introCall',
        header: 'Intro call',
        accessorFn: (row) => row?.introCall?.start ?? '',
        sortingFn: 'datetime',
        Cell: ({ row }) => <PersonCell text={formatListDate(row.original.introCall?.start)} name={row.original.rep} />,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row?.status ?? '',
      },
      {
        id: 'likelihood',
        header: 'Likelihood',
        accessorFn: (row) => row?.likelihood?.likelihood ?? '',
        // Takes no colour on purpose: a High on its own is not actionable, since
        // plenty of high-likelihood leads already have a consult booked. The
        // signal is High *and* nothing booked, which is the third chip.
        Cell: ({ row }) => {
          const { likelihood, barriers } = row.original?.likelihood ?? { likelihood: undefined, barriers: [] };

          if (!likelihood) {
            return '—';
          }

          return (
            <span>
              {likelihood}
              {barriers.length > 0 && (
                <Text component="span" c="dimmed" ml={6}>
                  {barriers.join(', ')}
                </Text>
              )}
            </span>
          );
        },
      },
      {
        id: 'consultDate',
        header: 'Consult date',
        accessorFn: (row) => row?.consult?.start ?? '',
        sortingFn: 'datetime',
        Cell: ({ row }) => <PersonCell text={formatListDate(row.original.consult?.start)} name={row.original.np} />,
      },
      lastCommunicationColumn<PreConsultationLeadRow>(),
      nextTaskColumn<PreConsultationLeadRow>(now),
    ],
    [now]
  );
}
